/**
 * useWatchRules — React hook for reading and updating watch rules.
 *
 * Single source of truth for strategy thresholds and notification settings.
 * Replaces useUpdateAccountFormulaSettings for strategy data.
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ─── Types ───────────────────────────────────────────────────────────

export interface WatchRule {
  id: number;
  account_id: number;
  formula_id: number | null;
  formula_code?: string;
  formula_name?: string;
  name: string;
  category: string;
  rule_type: string;
  severity: string;
  condition: Record<string, any>;
  channels: Record<string, any>;
  assignees: Record<string, any>;
  enabled: boolean;
  cooldown_hours: number;
  created_at: string;
  updated_at: string;
}

export interface ConditionField {
  key: string;
  label: string;
  type: 'number' | 'percent' | 'currency' | 'select' | 'number_array';
  unit?: string;
  default?: any;
  options?: { value: string; label: string }[];
  description?: string;
  /** If true, field is shown in a collapsible "Advanced" section in the edit modal */
  advanced?: boolean;
}

export interface RuleTypeMeta {
  ruleType: string;
  category: string;
  label: string;
  description: string;
  conditionSchema: ConditionField[];
  defaultCondition: Record<string, any>;
}

// ─── Auth helper ────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ─── API calls ──────────────────────────────────────────────────────

async function fetchWatchRules(accountId: string, filters?: { category?: string; formula_id?: number; enabled?: boolean }): Promise<WatchRule[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ accountId });
  if (filters?.category) params.set('category', filters.category);
  if (filters?.formula_id !== undefined) params.set('formula_id', String(filters.formula_id));
  if (filters?.enabled !== undefined) params.set('enabled', String(filters.enabled));

  const res = await fetch(`${API_URL}/api/v2/watch/rules?${params}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch watch rules');
  const data = await res.json();
  return data.rules || [];
}

async function patchWatchRule(ruleId: number, accountId: string, updates: Partial<WatchRule>): Promise<WatchRule> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/watch/rules/${ruleId}?accountId=${accountId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update watch rule');
  const data = await res.json();
  return data.rule;
}

async function createWatchRuleApi(accountId: string, rule: Partial<WatchRule>): Promise<WatchRule> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/watch/rules?accountId=${accountId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(rule),
  });
  if (!res.ok) throw new Error('Failed to create watch rule');
  const data = await res.json();
  return data.rule;
}

async function seedRules(accountId: string, reset = false): Promise<{ rules: WatchRule[]; deleted: number }> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/watch/rules/seed?accountId=${accountId}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reset }),
  });
  if (!res.ok) throw new Error('Failed to seed rules');
  const data = await res.json();
  return { rules: data.rules || [], deleted: data.deleted || 0 };
}

async function fetchRuleTypes(): Promise<RuleTypeMeta[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/watch/rule-types`, { headers });
  if (!res.ok) throw new Error('Failed to fetch rule types');
  const data = await res.json();
  return data.ruleTypes || [];
}

// ─── Hooks ──────────────────────────────────────────────────────────

/** Fetch all watch rules for an account, optionally filtered. */
export function useWatchRules(accountId: string | undefined, filters?: { category?: string; formula_id?: number }) {
  return useQuery(
    ['watch-rules', accountId, filters?.category, filters?.formula_id],
    () => fetchWatchRules(accountId!, filters),
    { enabled: !!accountId, staleTime: 30_000 },
  );
}

/** Fetch the rule type catalog (schemas, defaults). */
export function useRuleTypes() {
  return useQuery('watch-rule-types', fetchRuleTypes, { staleTime: 300_000 });
}

/** Mutation to update a watch rule's condition (or any other fields). */
export function useUpdateWatchRule(accountId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation(
    ({ ruleId, updates }: { ruleId: number; updates: Partial<WatchRule> }) =>
      patchWatchRule(ruleId, accountId!, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['watch-rules', accountId]);
      },
    },
  );
}

/** Mutation to create a new watch rule. */
export function useCreateWatchRule(accountId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation(
    (rule: Partial<WatchRule>) => createWatchRuleApi(accountId!, rule),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['watch-rules', accountId]);
      },
    },
  );
}

/** Mutation to seed default rules. Pass { reset: true } to delete all existing rules first. */
export function useSeedWatchRules(accountId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation(
    (opts?: { reset?: boolean }) => seedRules(accountId!, opts?.reset ?? false),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['watch-rules', accountId]);
      },
    },
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

/** Map from sub-tab key → rule_type(s) that power it. */
export const SUB_TAB_RULE_TYPES: Record<string, string[]> = {
  buffer: ['buffer_breach'],
  logistics: ['logistics_shortfall'],
  coverage: ['coverage_low'],
  exposure: ['exposure_threshold'],
  'hedge-price': ['cost_basis_drift', 'volatility_spike'],
  m2m: ['m2m_pnl'],
};

/** Find the first matching rule for a sub-tab + optional formula. */
export function findRuleForTab(
  rules: WatchRule[],
  subTab: string,
  formulaId?: number | null,
): WatchRule | undefined {
  const ruleTypes = SUB_TAB_RULE_TYPES[subTab];
  if (!ruleTypes) return undefined;

  // Prefer formula-specific rule, fall back to account-wide (formula_id = null)
  for (const rt of ruleTypes) {
    const specific = rules.find(r => r.rule_type === rt && r.formula_id === formulaId);
    if (specific) return specific;
    const accountWide = rules.find(r => r.rule_type === rt && r.formula_id === null);
    if (accountWide) return accountWide;
  }
  return undefined;
}

/** Find ALL matching rules for a sub-tab (supports multi-rule panels). */
export function findAllRulesForTab(
  rules: WatchRule[],
  subTab: string,
  formulaId?: number | null,
): WatchRule[] {
  const ruleTypes = SUB_TAB_RULE_TYPES[subTab];
  if (!ruleTypes) return [];

  const result: WatchRule[] = [];
  for (const rt of ruleTypes) {
    const specific = rules.find(r => r.rule_type === rt && r.formula_id === formulaId);
    if (specific) { result.push(specific); continue; }
    const accountWide = rules.find(r => r.rule_type === rt && r.formula_id === null);
    if (accountWide) result.push(accountWide);
  }
  return result;
}

/** Single-source policy config extracted from watch rules. */
export interface PolicyConfig {
  buffer: number | { baseAmount: number; lookaheadMonths: number; safetyPercentage: number };
  coverageTargets: number[];
  coverageIsPercent: boolean;
  logisticsTargets: number[];
  logisticsIsPercent: boolean;
  exposureThresholds: { dangerLow: number; warnLow: number; warnHigh: number; dangerHigh: number };
  m2mThresholds: { lossThreshold: number; marginExcessMin: number };
  driftThreshold: number;
}

export function extractPolicyConfig(rules: WatchRule[], formulaId?: number | null): PolicyConfig {
  const findCondition = (ruleType: string): Record<string, any> => {
    const specific = rules.find(r => r.rule_type === ruleType && r.formula_id === formulaId);
    if (specific) return specific.condition || {};
    const accountWide = rules.find(r => r.rule_type === ruleType && r.formula_id === null);
    return accountWide?.condition || {};
  };

  const bufferCond = findCondition('buffer_breach');
  const coverageCond = findCondition('coverage_low');
  const logisticsCond = findCondition('logistics_shortfall');
  const exposureCond = findCondition('exposure_threshold');

  // Buffer: support both simple (just buffer_mt) and dynamic (base + lookahead + safety%)
  const bufferMt = Number(bufferCond.buffer_mt) || 0;
  const lookaheadMonths = Number(bufferCond.lookahead_months) || 0;
  const safetyPercentage = Number(bufferCond.safety_percentage) || 0;

  // If dynamic buffer is configured, return as config object; otherwise just the number
  const bufferStrategy = (lookaheadMonths > 0 && safetyPercentage > 0)
    ? { baseAmount: bufferMt, lookaheadMonths, safetyPercentage }
    : bufferMt;

  const m2mCond = findCondition('m2m_pnl');
  const driftCond = findCondition('cost_basis_drift');

  return {
    buffer: bufferStrategy,
    coverageTargets: Array.isArray(coverageCond.values) && coverageCond.values.length > 0
      ? coverageCond.values as number[]
      : [],
    coverageIsPercent: true,
    logisticsTargets: Array.isArray(logisticsCond.values) && logisticsCond.values.length > 0
      ? logisticsCond.values as number[]
      : [],
    logisticsIsPercent: logisticsCond.is_percent !== false,
    exposureThresholds: {
      dangerLow: Number(exposureCond.danger_below) || 0,
      warnLow: Number(exposureCond.warn_below) || 0,
      warnHigh: Number(exposureCond.warn_above) || 0,
      dangerHigh: Number(exposureCond.danger_above) || 0,
    },
    m2mThresholds: {
      lossThreshold: Number(m2mCond.loss_threshold) || 0,
      marginExcessMin: Number(m2mCond.margin_excess_min) || 0,
    },
    driftThreshold: Number(driftCond.drift_percent) || 10,
  };
}
